import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Share } from '../../../../../utils/Share'
import { useTw } from '../../../../../utils/Styles'
import { Avatars } from '../../../../../utils/Avatars'
import { useProfile } from '../../../../../utils/Profile'
import { StaticRenderer } from '../../../../../utils/StaticRenderer'
import Wrapper, { LoadingWrapper } from '../../../../../utils/Wrapper'
import { localizeString, localizeContent } from '../../../../../utils'
import {
  useKnowledgeBaseQuery,
  KnowledgeBaseQuery,
} from '../KnowledgeBase.generated'

type ArticleHeaderProps = {
  article: KnowledgeBaseQuery['Profile']['knowledgeBase']['categories'][0]['articles'][0]
}

export function ArticleHeader({ article }: ArticleHeaderProps) {
  const tw = useTw()
  const { account } = useProfile()
  const [t] = useTranslation('common')
  const users = [
    article.createdBy,
    article.createdBy._id === article.publishedBy._id
      ? undefined
      : article.publishedBy,
  ]

  const shareBaseUrl = account?.clientPortal?.customDomain
    ? `https://${account?.clientPortal?.customDomain}`
    : `https://${account.slug}.firesquad.io`

  return (
    <header className={tw`space-y-4 pb-4 border-b border-neutral-200`}>
      <h1 className={tw`text-xl text-neutral-500`}>
        {localizeString(article.title)}
      </h1>
      <p className={tw`text-neutral-350`}>{localizeString(article.subtitle)}</p>
      <div className={tw`flex justify-between items-center pt-4`}>
        <div className={tw`flex flex-row select-none`}>
          <Avatars users={users} />
          <div className={tw`ml-2`}>
            <p className={tw`text-sm text-neutral-400 font-bold`}>
              {t('home.kb_article.written_by')}
            </p>
            <p className={tw`text-sm text-neutral-350`}>
              {users
                .filter((_) => _?._id)
                .map((_) => _.name)
                .join(' & ')}
            </p>
          </div>
        </div>
        {account?.clientPortal?.knowledgeBaseEnabled ? (
          <Share
            title={localizeString(article.title)}
            text={localizeString(article.subtitle)}
            url={`${shareBaseUrl}/category/${article.categoryId}/article/${article.articleId}`}
          />
        ) : null}
      </div>
    </header>
  )
}

export function ArticleView() {
  const tw = useTw()
  const [t] = useTranslation('common')
  const { data, loading } = useKnowledgeBaseQuery()
  const { categoryId, articleId } = useParams()

  if (loading) return <LoadingWrapper title={t('knowledge_base.title')} />

  const category = data?.Profile?.knowledgeBase?.categories.find(
    (_) => _._id === categoryId
  )
  const article = category?.articles?.find((_) => _.articleId === articleId)

  return (
    <Wrapper
      title={t('knowledge_base.title')}
      subtitle={localizeString(article.title)}
    >
      <section className={tw`p-6`}>
        <ArticleHeader article={article} />
        <article className={tw`pt-4`}>
          <StaticRenderer
            content={localizeContent(article.content)}
            theme={{
              heading: {
                h1: tw`text-3xl`,
                h2: tw`text-2xl`,
                h3: tw`text-xl`,
                h4: tw`text-lg`,
              },
              embedBlock: {
                base: tw`relative pt-[75%]`,
                focus: tw`ring ring-deep-purple-500`,
              },
              image: tw`flex flex-col pt-1 items-center`,
              imageTag: tw`w-full object-cover rounded`,
              embedBlockFrame: tw`absolute top-0 left-0 w-full h-full`,
              list: { ul: tw`list-disc pl-4`, ol: tw`list-decimal pl-4` },
              codeblock: tw`px-2 bg-gray-200 rounded-sm font-mono`,
              quote: tw`border-l-2 border-gray-300 text-gray-500 pl-2 first-of-type:mt-2 last-of-type:mb-2`,
            }}
          />
        </article>
      </section>
    </Wrapper>
  )
}
